import React, { useCallback, useEffect, useState } from "react";

import {
  EmotionComponent,
  keyframes,
  styled,
  theme,
} from "@catchoftheday/theme";

import { Box } from "../Box";
import { Flex } from "../Flex";
import { IconAlert, IconProps, IconTick, IconToolTip, IconX } from "../Icon";

import { InlineAlert } from "./InlineAlert";
import {
  AlertProps,
  CloseIconProps,
  PointerProps,
  PopupWrapperProps,
  WrapperProps,
} from "./types";

const TRANSITION_DURATION = 300;
const fadeIn = keyframes`
    0% { opacity: 0; }
    100% { opacity: 1; }
`;
const fadeOut = keyframes`
    0% { opacity: 1; }
    100% { opacity: 0; }
`;

export const ALERT_VARIANT_MAP: {
  [k: string]: {
    color: string;
    icon: React.FC<IconProps>;
  };
} = {
  error: {
    color: "error",
    icon: IconAlert,
  },
  warning: {
    color: "warning",
    icon: IconAlert,
  },
  success: {
    color: "primary",
    icon: IconTick,
  },
  default: {
    color: "text",
    icon: IconToolTip,
  },
};

const PopupWrapper: EmotionComponent<PopupWrapperProps> = styled(
  Box
)<PopupWrapperProps>`
  position: relative;
  animation: ${(props) => (props.show ? fadeIn : fadeOut)}
    ${TRANSITION_DURATION}ms ease;
`;

const Pointer: EmotionComponent<PointerProps> = styled(Box)`
  ${(props) => `
  height: ${props.size};
  width: ${props.size};
  border-left: ${props.size} solid transparent;
  border-right: ${props.size} solid transparent;
  border-bottom-width: ${props.size};
  border-bottom-style: solid;
  filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.25));`}
`;

Pointer.defaultProps = {
  size: "7px",
  borderBottomColor: "white",
  ml: "50%",
};

const CloseIconContainer = styled(Box)`
  :focus {
    outline: 1px solid ${theme.colors.secondary};
  }
`;

const CloseIcon = ({ type, hidePopup, ...props }: CloseIconProps) => (
  <CloseIconContainer
    role="button"
    aria-label="close button"
    tabIndex={0}
    position="absolute"
    top={type == "inline" ? "12px" : "3px"}
    right="3px"
    cursor="pointer"
    color="text"
    zIndex={1}
    onClick={hidePopup}
    {...props}
  >
    <IconX fill="currentColor" />
  </CloseIconContainer>
);

const Overlay: React.FC<WrapperProps> = ({
  variant,
  containerProps,
  children,
}) => (
  <Box
    role="alert"
    position="relative"
    fontSize="xs"
    py="16px"
    pl="10px"
    pr="28px"
    borderRadius="default"
    backgroundColor="white"
    boxShadow="dropDown"
    {...ALERT_VARIANT_MAP[variant]}
    {...containerProps}
  >
    {children}
  </Box>
);

const InlineSmall: React.FC<WrapperProps> = ({
  variant,
  containerProps,
  showAlertIcon,
  icon,
  children,
}) => {
  const AlertIcon = ALERT_VARIANT_MAP[variant].icon;
  const alertColor = ALERT_VARIANT_MAP[variant].color;
  return (
    <Flex role="alert" width="auto">
      {showAlertIcon && (
        <Box color={theme.colors[alertColor]}>
          {icon ?? (
            <AlertIcon
              fill={theme.colors[alertColor]}
              height="14px"
              width="14px"
            />
          )}
        </Box>
      )}
      <Flex
        alignItems="center"
        fontSize={{ xs: "2xs", sm: "xs" }}
        mt="4px"
        ml="4px"
        {...containerProps}
      >
        {children}
      </Flex>
    </Flex>
  );
};

export const Alert: React.FC<AlertProps> = ({
  type = "overlay",
  variant = "default",
  theme,
  timeoutDuration,
  showPointer,
  showCloseButton,
  showAlertIcon = true,
  alignIcon,
  icon,
  pointerProps,
  wrapperProps,
  children,
  hideAlert,
  ...containerProps
}) => {
  const [showPopup, setShowPopup] = useState(true);

  const hidePopup = useCallback(() => {
    setShowPopup(false);
    setTimeout(() => {
      hideAlert && hideAlert();
    }, TRANSITION_DURATION);
  }, [hideAlert]);

  useEffect(() => {
    if (timeoutDuration) {
      const timeout = setTimeout(() => {
        hidePopup();
      }, timeoutDuration);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [hidePopup, timeoutDuration]);

  const ErrorWrapper =
    type == "small" ? InlineSmall : type == "inline" ? InlineAlert : Overlay;

  return (
    <PopupWrapper
      role="dialog"
      aria-label="alert wrapper"
      show={showPopup}
      width="100%"
      {...wrapperProps}
    >
      {showPointer && <Pointer {...pointerProps} aria-label="alert pointer" />}
      <ErrorWrapper
        variant={variant}
        containerProps={containerProps}
        showAlertIcon={showAlertIcon}
        alignIcon={alignIcon}
        icon={icon}
        theme={theme}
      >
        {children}
        {showCloseButton && (
          <CloseIcon
            type={type}
            hidePopup={hidePopup}
            color={theme === "dark" ? "white" : "text"}
          />
        )}
      </ErrorWrapper>
    </PopupWrapper>
  );
};

export default Alert;
